import React, { useMemo } from 'react';
import './HowFoundUsDropdown.css';

/**
 * Attribution options for the "How did you find us?" survey.
 *
 * WHY THIS EXISTS: 78.5% of BotSpot paying customers had no attribution source
 * in PostHog. This self-reported question closes the gap so we know which channels
 * are actually driving revenue.
 *
 * Options are randomized on mount to eliminate order bias. "Other" is always
 * anchored at the bottom (industry best practice - it's an escape valve, not a choice).
 */
const ATTRIBUTION_OPTIONS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'google_search', label: 'Google search' },
  { value: 'github', label: 'GitHub' },
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'claude', label: 'Claude / Claude.ai' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'codex', label: 'Codex CLI' },
  { value: 'smithery', label: 'Smithery' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'twitter_x', label: 'Twitter / X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'friend_colleague', label: 'Friend or colleague' },
  { value: 'affiliate_link', label: 'Affiliate Link' },
  { value: 'lumibot_docs', label: 'Lumibot documentation' },
  { value: 'lumiwealth_course', label: 'Lumiwealth course / past customer' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'email', label: 'Email newsletter' },
  { value: 'other', label: 'Other' }, // ALWAYS last - never randomized
];

function shuffleOptions(options) {
  const main = [...options.slice(0, -1)]; // All except "Other"
  // Fisher-Yates shuffle to eliminate order bias in responses
  for (let i = main.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [main[i], main[j]] = [main[j], main[i]];
  }
  return [...main, options[options.length - 1]]; // "Other" always at bottom
}

/**
 * HowFoundUsDropdown - Required attribution dropdown for checkout.
 *
 * @param {string} value - Selected option value (e.g. 'youtube', 'other')
 * @param {string} otherText - Free-text when value === 'other'
 * @param {function} onChange - Called with the selected value string
 * @param {function} onOtherTextChange - Called with the typed text when 'Other' is selected
 * @param {boolean} disabled - Disable all inputs (e.g. while processing)
 */
export default function HowFoundUsDropdown({ value, otherText, onChange, onOtherTextChange, disabled = false }) {
  // Randomize once on mount - useMemo with no deps so it doesn't re-shuffle on re-renders
  const shuffledOptions = useMemo(() => shuffleOptions(ATTRIBUTION_OPTIONS), []);

  return (
    <div className="how-found-us-field">
      <label className="how-found-us-label" htmlFor="how-found-us-select">
        How did you find us? <span className="how-found-us-required">*</span>
      </label>
      <select
        id="how-found-us-select"
        className="how-found-us-select"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required
        disabled={disabled}
        aria-required="true"
      >
        <option value="" disabled>Select one...</option>
        {shuffledOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {value === 'other' && (
        <input
          type="text"
          className="how-found-us-other-input"
          placeholder="Please tell us how you found BotSpot..."
          value={otherText || ''}
          onChange={(e) => onOtherTextChange(e.target.value)}
          required
          disabled={disabled}
          aria-label="How did you find us (please describe)"
          data-hj-allow="true"
        />
      )}
    </div>
  );
}
